// src/App.tsx
import { BrowserRouter, useRoutes } from "react-router-dom";
import appRoutes from "./routes/appRoutes";
import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setUser,
  clearUser,
  setLoadedFromFirestore,
  setLoading,
  setRoleConfirmed,
} from "./store/slices/authSlice";
import type { AppUser } from "./types/User";

import { auth, db } from "./config/firebaseConfig";
import { onAuthStateChanged, getIdTokenResult } from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";
import { ToastProvider } from "./util/ToastProvider";
import { setupUserVisitTracking } from "./util/logUserVisit";
import { RootState } from "./store";

function RoutesWrapper() {
  return useRoutes(appRoutes);
}

export default function App() {
  const dispatch = useDispatch();
  const appUser = useSelector((state: RootState) => state.auth.user);

  const unsubRef = useRef<null | (() => void)>(null);
  const roleConfirmedTimeoutRef = useRef<number | null>(null);

  const toAppUserFromDoc = (uid: string, d: any): AppUser => ({
    uid,
    email: d?.email ?? auth.currentUser?.email ?? null,
    displayName:
      d?.profile?.identity?.fullName ??
      d?.profile?.identity?.givenName ??
      auth.currentUser?.displayName ?? d?.name ??

      null,
    dob: d?.profile?.birth?.date ?? null,
    emailVerified: !!d?.emailVerified,
    basicInfoSetup: !!d?.basicInfoSetup,
    role: d?.role,
    hasReports: !!d?.hasReports,
    disabled: !!d?.disabled,
    helperText: d?.helperText ? {text: d?.helperText.text, image:d?.helperText.image}: undefined
  });

  const startUserDocListener = async (uid: string, seed?: Partial<AppUser>) => {
    console.log("[App] startUserDocListener - start", { uid, seed });

    dispatch(setLoadedFromFirestore(false));
    dispatch(setRoleConfirmed(false));

    if (seed) {
      console.log("[App] seed user -> setUser (snappy)");
      dispatch(
        setUser({
          uid,
          email: seed.email ?? null,
          displayName: seed.displayName ?? null,
          dob: seed.dob ?? null,
          emailVerified: !!seed.emailVerified,
          basicInfoSetup: !!seed.basicInfoSetup,
          role: (seed.role as AppUser["role"]) ?? "user",
        })
      );
    }

    const ref = doc(db, "users", uid);

    if (unsubRef.current) {
      try {
        unsubRef.current();
      } catch (e) {}
      unsubRef.current = null;
    }
    if (roleConfirmedTimeoutRef.current) {
      window.clearTimeout(roleConfirmedTimeoutRef.current);
      roleConfirmedTimeoutRef.current = null;
    }

    const ROLE_CONFIRMED_FALLBACK_MS = 5000;
    roleConfirmedTimeoutRef.current = window.setTimeout(() => {
      console.warn("[App] roleConfirmed fallback fired (timeout)");
      dispatch(setRoleConfirmed(true));
      roleConfirmedTimeoutRef.current = null;
    }, ROLE_CONFIRMED_FALLBACK_MS) as unknown as number;

    unsubRef.current = onSnapshot(
      ref,
      async (s) => {
        const d = s.data();
    
        dispatch(setLoadedFromFirestore(true));
        dispatch(setRoleConfirmed(true));
    
        if (roleConfirmedTimeoutRef.current) {
          window.clearTimeout(roleConfirmedTimeoutRef.current);
          roleConfirmedTimeoutRef.current = null;
        }
    
        console.log("value of d-11", d)
        if (!d) return;
    
        console.log("value of d-2", d)
        // 🚨 HARD LOGOUT CONDITION
        const isFromCache = s.metadata.fromCache;

        console.log("[App] snapshot fromCache:", isFromCache, d);
        
        if (!isFromCache && d.disabled === true) {
          console.warn("[App] User is disabled (server-confirmed) → hard logout");
        
          if (unsubRef.current) {
            unsubRef.current();
            unsubRef.current = null;
          }
        
          try {
            await auth.currentUser?.getIdToken(true);
          } catch {}
        
          dispatch(clearUser());
          await auth.signOut();
          return;
        }
        
        
    
        // ✅ Normal user flow
        dispatch(setUser(toAppUserFromDoc(uid, d)));
      },
      (err) => {
        console.error("[App] onSnapshot error:", err);
        dispatch(setLoadedFromFirestore(true));
        dispatch(setRoleConfirmed(true));
      }
    );
    
  };

  useEffect(() => {
    dispatch(setLoading(true));
    dispatch(setLoadedFromFirestore(false));
    dispatch(setRoleConfirmed(false));

    const unsubAuth = onAuthStateChanged(auth, async (fbUser) => {
      console.log("[App] onAuthStateChanged -> fired", { fbUserExists: !!fbUser });

      if (!fbUser) {
        if (unsubRef.current) {
          try {
            unsubRef.current();
          } catch (e) {}
          unsubRef.current = null;
        }
        if (roleConfirmedTimeoutRef.current) {
          window.clearTimeout(roleConfirmedTimeoutRef.current);
          roleConfirmedTimeoutRef.current = null;
        }
        dispatch(clearUser());
        return;
      }

      // ✅ Force refresh & confirm claims before starting Firestore listener
      const tokenResult = await getIdTokenResult(fbUser, true);
      console.log("[App] token claims:", tokenResult.claims);

      if (!tokenResult.claims.role) {
        console.warn("[App] No role claim found, signing out...");
        await auth.signOut();
        return;
      }

      await startUserDocListener(fbUser.uid, {
        uid: fbUser.uid,
        email: fbUser.email ?? null,
        displayName: fbUser.displayName ?? null,
        emailVerified: fbUser.emailVerified ?? false,
      });
    });

    return () => {
      console.log("[App] cleanup - unsubAuth");
      unsubAuth();
      if (unsubRef.current) {
        try {
          unsubRef.current();
        } catch (e) {}
        unsubRef.current = null;
      }
      if (roleConfirmedTimeoutRef.current) {
        window.clearTimeout(roleConfirmedTimeoutRef.current);
        roleConfirmedTimeoutRef.current = null;
      }
    };
  }, [dispatch]);

  useEffect(() => {
    if(appUser){
      setupUserVisitTracking(appUser);
    }
      
  }, [appUser]);

  return (
    <ToastProvider>
      <BrowserRouter>
        <RoutesWrapper />
      </BrowserRouter>
    </ToastProvider>
  );
}
